import type { SignupCadence, SignupCard } from '../plans/signupPlans.types'

/** A chosen tier whose price has no amount. Nothing the customer can fix, and refusing
 *  in silence leaves them clicking a button that does nothing. One message for the signup
 *  payment page and the readiness plans page, so the two surfaces never disagree. */
export const PLAN_UNAVAILABLE_MESSAGE =
  "That plan isn't available right now. Refresh and try again, or contact support and we'll sort it out."

/** A stored plan the list no longer offers, so the visitor chooses again. */
export const CHOSEN_PLAN_NOT_OFFERED_MESSAGE =
  "We couldn't find the plan you chose. Choose a plan to continue."

/** Plans are on offer but none was chosen and none can be picked for the visitor. */
export const NO_PLAN_CHOSEN_MESSAGE = 'Choose a plan to continue.'

/**
 * The product the selected cadence is sold as. On the flag-off (legacy) path each cadence
 * can be a different Stripe product with its own permission grants, so the card's own
 * `productId`, an arbitrary first-seen value, is only the fallback.
 */
export const cadenceProductIdFor = (
  card: SignupCard,
  cadence: SignupCadence
): string => card.prices[cadence]?.productId ?? card.productId

export type CadencePriceView = { productId: string; unitAmountInCents: number }

/**
 * The plan's price for a cadence, or undefined when it lists none or lists one with no amount.
 * A missing amount is never coerced to 0, which would render and let checkout run on a free sale.
 */
export const cadencePriceView = (
  card: SignupCard,
  cadence: SignupCadence
): CadencePriceView | undefined => {
  const price = card.prices[cadence]
  return price && typeof price.unitAmountInCents === 'number'
    ? { productId: price.productId, unitAmountInCents: price.unitAmountInCents }
    : undefined
}
